import { useEffect } from "react";
import { getSupportWidgetIdentity } from "../api/supportWidgetService";

// Base URL of the hosted widget bundle. The script renders inside a Shadow DOM
// and injects its own styles, so there's nothing else to load.
const WIDGET_HOST = "https://support.widget.devsoleil.com";
const PRODUCT = "smart-ca";
const SCRIPT_ID = "hub-widget-js";

// Loads the Support Hub chat widget for the signed-in user.
//
// Our users have no email — they're identified by phone — so the server signs
// `external_id = phone` and we hand the widget that identifier plus its hash.
// A matching hash marks the session "verified", unlocking cross-device history.
//
// Pass `enabled = false` to skip loading (e.g. for roles that shouldn't see
// the widget). When it flips to false we also remove the injected script so the
// launcher disappears.
export function useSupportWidget(enabled = true) {
  useEffect(() => {
    if (!enabled) {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) existing.remove();
      delete window.HubWidgetConfig;
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        // The interceptor unwraps the envelope, so identity sits at `res.data`.
        const res = await getSupportWidgetIdentity();
        const identity = res?.data;
        if (cancelled || !identity) return;

        // Must be set before the script loads — the widget reads it at load time.
        window.HubWidgetConfig = {
          product: identity.product || PRODUCT,
          key: identity.key,
          name: identity.name,
          external_id: identity.external_id,
          user_hash: identity.user_hash, // ← marks the session "verified"
        };

        if (!document.getElementById(SCRIPT_ID)) {
          const s = document.createElement("script");
          s.id = SCRIPT_ID;
          s.src = `${WIDGET_HOST}/hub-widget.js`;
          s.async = true;
          document.body.appendChild(s);
        }
      } catch {
        // Silent — the widget is non-critical; a 403/secret-rotation just means
        // no chat. The auth interceptor handles 401s.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
